import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { User, Briefcase, LogOut, Edit2, Loader2, Info, Globe, Bell, Compass, Moon, BookOpen, ArrowRight, Zap, CalendarClock, Sun, Boxes, Hammer, Calculator, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { usePushNotifications } from '../hooks/usePushNotifications';

const Profile = () => {
  const navigate = useNavigate();
  const { user, updateProfile, logout, api } = useAuth();
  const { t, lang } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    business_context: user?.business_context || '',
    sector: user?.sector || '',
    language: user?.language || 'fr'
  });
  const [saving, setSaving] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [boussoleSettings, setBoussoleSettings] = useState({ enabled: true, hour: 7, timezone: 'Europe/Paris' });
  const [boussoleLoading, setBoussoleLoading] = useState(true);
  const [debriefSettings, setDebriefSettings] = useState({ enabled: true, hour: 17, timezone: 'Europe/Paris' });
  const [captureSettings, setCaptureSettings] = useState({ fab_visible: true });
  const [modulesEnabled, setModulesEnabled] = useState(user?.modules_enabled || { team: true, cfo: true, studio: true });
  const [sectorDefaults, setSectorDefaults] = useState(null);
  const [modulesLoading, setModulesLoading] = useState(false);

  // Load modules state + sector defaults
  useEffect(() => {
    api.get('/api/auth/modules')
      .then(r => {
        setModulesEnabled(r.data?.modules_enabled || { team: true, cfo: true, studio: true });
        setSectorDefaults(r.data?.sector_defaults || null);
      })
      .catch(() => {});
  }, [api]);

  const toggleModule = async (key) => {
    const next = { ...modulesEnabled, [key]: !modulesEnabled[key] };
    setModulesEnabled(next);
    setModulesLoading(true);
    try {
      await api.put('/api/auth/modules', { [key]: next[key] });
      toast.success(next[key] ? 'Module activé' : 'Module désactivé');
      // Refresh user pour que la Sidebar/MobileNav réagisse
      await updateProfile({});
    } catch {
      setModulesEnabled(modulesEnabled);
      toast.error('Échec mise à jour module');
    } finally { setModulesLoading(false); }
  };

  // Load sector list once
  useEffect(() => {
    api.get('/api/sectors')
      .then(r => setSectors(r.data?.sectors || []))
      .catch(() => setSectors([]));
  }, [api]);

  // Sync formData when user changes (e.g. after refresh)
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        business_context: user.business_context || '',
        sector: user.sector || '',
        language: user.language || 'fr',
      });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile(formData);
      setIsEditing(false);
      if (formData.language !== user?.language) {
        toast.success(t('profile.languageUpdated'));
      }
    } catch (error) {
      console.error('Update error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleLanguageChange = async (newLang) => {
    try {
      await updateProfile({ language: newLang });
      toast.success(t('profile.languageUpdated'));
    } catch (error) {
      console.error('Language update error:', error);
    }
  };

  // Boussole settings
  useEffect(() => {
    const fetchBoussoleSettings = async () => {
      try {
        const res = await api.get('/api/boussole/settings');
        setBoussoleSettings(res.data);
      } catch (e) {
        console.error('Failed to load boussole settings');
      } finally {
        setBoussoleLoading(false);
      }
    };
    const fetchDebriefSettings = async () => {
      try {
        const res = await api.get('/api/debrief/settings');
        setDebriefSettings(res.data);
      } catch (e) {
        console.error('Failed to load debrief settings');
      }
    };
    const fetchCaptureSettings = async () => {
      try {
        const res = await api.get('/api/capture/settings');
        setCaptureSettings(res.data);
      } catch (e) { /* default */ }
    };
    fetchBoussoleSettings();
    fetchDebriefSettings();
    fetchCaptureSettings();
  }, [api]);

  const updateBoussoleSetting = async (key, value) => {
    const newSettings = { ...boussoleSettings, [key]: value };
    setBoussoleSettings(newSettings);
    try {
      await api.put('/api/boussole/settings', { [key]: value });
    } catch (e) {
      setBoussoleSettings(boussoleSettings);
      toast.error(t('common.error'));
    }
  };

  const updateDebriefSetting = async (key, value) => {
    const prev = { ...debriefSettings };
    setDebriefSettings({ ...debriefSettings, [key]: value });
    try {
      await api.put('/api/debrief/settings', { [key]: value });
    } catch (e) {
      setDebriefSettings(prev);
      toast.error(t('common.error'));
    }
  };

  const updateCaptureSetting = async (key, value) => {
    const prev = { ...captureSettings };
    setCaptureSettings({ ...captureSettings, [key]: value });
    try {
      await api.put('/api/capture/settings', { [key]: value });
    } catch (e) {
      setCaptureSettings(prev);
      toast.error(t('common.error'));
    }
  };

  const handleLogout = () => {
    logout();
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div data-testid="profile-page" className="p-6 lg:p-8 max-w-2xl mx-auto">
      {/* Profile Header */}
      <div className="glass-card p-8 text-center mb-8">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-atlasCyan to-atlasPurple flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl font-bold text-atlasBg atlas-brand">
            {getInitials(user?.name)}
          </span>
        </div>
        <h1 className="text-2xl font-bold text-white mb-1">{user?.name}</h1>
        <p className="text-atlasTextSecondary">{user?.email}</p>
      </div>

      {/* Language Selector Card */}
      <div className="glass-card p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-atlasCyan" />
            <div>
              <h3 className="text-white font-medium">{t('auth.language')}</h3>
              <p className="text-atlasTextSecondary text-sm">{t('auth.selectLanguage')}</p>
            </div>
          </div>
          <Select value={user?.language || 'fr'} onValueChange={handleLanguageChange}>
            <SelectTrigger data-testid="profile-language-select" className="w-40 input-atlas">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fr">🇫🇷 Français</SelectItem>
              <SelectItem value="en">🇬🇧 English</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Profile Details */}
      <div className="glass-card p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <User className="w-5 h-5 text-atlasCyan" />
            {t('profile.information')}
          </h2>
          <Button 
            variant="ghost" 
            size="sm" 
            data-testid="edit-profile-btn"
            onClick={() => setIsEditing(true)}
            className="text-atlasCyan"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            {t('profile.editProfile')}
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-atlasTextSecondary text-sm">{t('profile.fullName')}</Label>
            <p className="text-white mt-1">{user?.name}</p>
          </div>
          <div>
            <Label className="text-atlasTextSecondary text-sm">{t('auth.email')}</Label>
            <p className="text-white mt-1">{user?.email}</p>
          </div>
          <div>
            <Label className="text-atlasTextSecondary text-sm flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              {t('auth.businessContext')}
            </Label>
            <p className="text-white mt-1">
              {user?.business_context || <span className="text-atlasTextSecondary italic">-</span>}
            </p>
          </div>
          <div>
            <Label className="text-atlasTextSecondary text-sm flex items-center gap-2">
              <Compass className="w-4 h-4" />
              Secteur d'activité
            </Label>
            <p className="text-white mt-1">
              {sectors.find(s => s.key === user?.sector)?.label
                || <span className="text-atlasTextSecondary italic">Non renseigné — ATLAS adoptera un raisonnement transverse</span>}
            </p>
          </div>
        </div>
      </div>

      {/* === Modules activés === */}
      <div data-testid="modules-section" className="glass-card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Boxes className="w-5 h-5 text-atlasCyan" />
            Modules activés
          </h2>
          {sectorDefaults && user?.sector && (
            <span className="text-[11px] text-atlasTextSecondary">
              Défaut secteur : {sectors.find(s => s.key === user?.sector)?.label || user?.sector}
            </span>
          )}
        </div>
        <p className="text-atlasTextSecondary text-sm mb-5">Active uniquement ce dont tu as besoin pour garder une interface épurée.</p>
        <ul className="space-y-3">
          {[
            { key: 'team',   icon: Hammer,     label: 'Équipe & Chantiers', desc: 'Pointage GPS, checklists qualité, gestion ouvriers' },
            { key: 'cfo',    icon: Calculator, label: 'CFO',                desc: 'Cash-flow, BFR, devis cash-aware, garde-fou stratégique' },
            { key: 'studio', icon: Sparkles,   label: 'Studio Marketing',   desc: 'Génération posts LinkedIn et pitch decks par l\'IA' },
          ].map(m => {
            const Icon = m.icon;
            const enabled = !!modulesEnabled[m.key];
            const isDefault = sectorDefaults && sectorDefaults[m.key] === enabled;
            return (
              <li key={m.key} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/[0.02] border border-atlasBorder">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-atlasCyan/15 text-atlasCyan' : 'bg-white/5 text-atlasTextSecondary'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium text-sm">{m.label}{!isDefault && <span className="text-[10px] text-atlasTextSecondary ml-2">(personnalisé)</span>}</p>
                    <p className="text-xs text-atlasTextSecondary truncate">{m.desc}</p>
                  </div>
                </div>
                <button
                  data-testid={`module-toggle-${m.key}`}
                  onClick={() => !modulesLoading && toggleModule(m.key)}
                  disabled={modulesLoading}
                  aria-label={`Activer ${m.label}`}
                  className={`relative w-11 h-6 rounded-full transition flex-shrink-0 ${enabled ? 'bg-atlasCyan' : 'bg-white/10'} ${modulesLoading ? 'opacity-50' : ''}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${enabled ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </li>
            );
          })}
        </ul>
        <p className="text-[11px] text-atlasTextSecondary mt-4">Les modules désactivés disparaissent de la navigation. Tu peux les réactiver à tout moment.</p>
      </div>

      {/* About ATLAS */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Info className="w-5 h-5 text-atlasCyan" />
          {t('profile.aboutAtlas')}
        </h2>
        <p className="text-atlasTextSecondary leading-relaxed mb-4">
          {t('profile.aboutDescription')}
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="p-3 rounded-lg bg-atlasYellow/10">
            <p className="text-atlasYellow font-semibold">{t('profile.modes.morning')}</p>
            <p className="text-xs text-atlasTextSecondary">{t('profile.modes.morningDesc')}</p>
          </div>
          <div className="p-3 rounded-lg bg-atlasCyan/10">
            <p className="text-atlasCyan font-semibold">{t('profile.modes.decision')}</p>
            <p className="text-xs text-atlasTextSecondary">{t('profile.modes.decisionDesc')}</p>
          </div>
          <div className="p-3 rounded-lg bg-atlasPurple/10">
            <p className="text-atlasPurple font-semibold">{t('profile.modes.evening')}</p>
            <p className="text-xs text-atlasTextSecondary">{t('profile.modes.eveningDesc')}</p>
          </div>
        </div>
      </div>

      {/* Notifications & Rituals */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-atlasCyan" />
          {t('profile.notificationsRituals')}
        </h2>
        
        {/* Boussole du lundi toggle */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Compass className="w-5 h-5 text-atlasCyan" />
              <div>
                <p className="text-white font-medium">{t('boussole.title')}</p>
                <p className="text-atlasTextSecondary text-sm">{t('boussole.settingsDesc')}</p>
              </div>
            </div>
            <button
              data-testid="boussole-toggle"
              onClick={() => updateBoussoleSetting('enabled', !boussoleSettings.enabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                boussoleSettings.enabled ? 'bg-atlasCyan' : 'bg-atlasBorder'
              }`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                boussoleSettings.enabled ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>

          {boussoleSettings.enabled && (
            <div className="flex items-center justify-between pl-8">
              <div>
                <p className="text-white text-sm">{t('profile.boussoleHour')}</p>
                <p className="text-atlasTextSecondary text-xs">{t('profile.boussoleHourDesc')}</p>
              </div>
              <Select 
                value={String(boussoleSettings.hour)} 
                onValueChange={(v) => updateBoussoleSetting('hour', parseInt(v))}
              >
                <SelectTrigger data-testid="boussole-hour-select" className="w-24 input-atlas">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[6, 7, 8, 9, 10].map(h => (
                    <SelectItem key={h} value={String(h)}>{h}h00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-atlasBorder" />

          {/* Debrief du vendredi toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Moon className="w-5 h-5 text-atlasPurple" />
              <div>
                <p className="text-white font-medium">{t('debrief.title')}</p>
                <p className="text-atlasTextSecondary text-sm">{t('debrief.settingsDesc')}</p>
              </div>
            </div>
            <button
              data-testid="debrief-toggle"
              onClick={() => updateDebriefSetting('enabled', !debriefSettings.enabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                debriefSettings.enabled ? 'bg-atlasPurple' : 'bg-atlasBorder'
              }`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                debriefSettings.enabled ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>

          {debriefSettings.enabled && (
            <div className="flex items-center justify-between pl-8">
              <div>
                <p className="text-white text-sm">{t('profile.debriefHour')}</p>
                <p className="text-atlasTextSecondary text-xs">{t('profile.debriefHourDesc')}</p>
              </div>
              <Select 
                value={String(debriefSettings.hour)} 
                onValueChange={(v) => updateDebriefSetting('hour', parseInt(v))}
              >
                <SelectTrigger data-testid="debrief-hour-select" className="w-24 input-atlas">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[16, 17, 18, 19, 20].map(h => (
                    <SelectItem key={h} value={String(h)}>{h}h00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-atlasBorder" />

          {/* Capture FAB toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5" style={{ color: '#FFD60A' }} />
              <div>
                <p className="text-white font-medium">{t('capture.fabToggleLabel')}</p>
                <p className="text-atlasTextSecondary text-sm">{t('capture.fabToggleDesc')}</p>
              </div>
            </div>
            <button
              data-testid="capture-fab-toggle"
              onClick={() => updateCaptureSetting('fab_visible', !captureSettings.fab_visible)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                captureSettings.fab_visible ? 'bg-yellow-500' : 'bg-atlasBorder'
              }`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                captureSettings.fab_visible ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
          {!captureSettings.fab_visible && (
            <p className="text-xs text-atlasTextSecondary pl-8">{t('capture.fabHiddenNote')}</p>
          )}

          {/* Divider */}
          <div className="border-t border-atlasBorder" />

          {/* Engagement push notifications (Phase B1) */}
          <EngagementNotificationsSection api={api} />

          {/* Divider */}
          <div className="border-t border-atlasBorder" />

          {/* Mode notifications (Matin / Décision / Soir) */}
          <ModeNotificationsSection api={api} />
        </div>
      </div>

      {/* Capture Journal Link */}
      <button
        data-testid="capture-journal-link"
        onClick={() => navigate('/captures')}
        className="glass-card p-5 mb-4 w-full flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <Zap className="w-5 h-5" style={{ color: '#FFD60A' }} />
          <div>
            <p className="text-white font-medium">{t('capture.journalTitle')}</p>
            <p className="text-atlasTextSecondary text-sm">{t('capture.journalSubtitle')}</p>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-atlasTextSecondary" />
      </button>

      {/* Engagements Link */}
      <button
        data-testid="engagements-link"
        onClick={() => navigate('/engagements')}
        className="glass-card p-5 mb-4 w-full flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <CalendarClock className="w-5 h-5 text-atlasCyan" />
          <div>
            <p className="text-white font-medium">{t('engagements.title')}</p>
            <p className="text-atlasTextSecondary text-sm">{t('engagements.subtitle')}</p>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-atlasTextSecondary" />
      </button>

      {/* Weekly Journal Link */}
      <button
        data-testid="weekly-journal-link"
        onClick={() => navigate('/journal')}
        className="glass-card p-5 mb-6 w-full flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-atlasCyan" />
          <div>
            <p className="text-white font-medium">{t('journal.title')}</p>
            <p className="text-atlasTextSecondary text-sm">{t('journal.subtitle')}</p>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-atlasTextSecondary" />
      </button>

      {/* Logout */}
      <Button 
        data-testid="logout-btn"
        onClick={() => setShowLogoutConfirm(true)}
        variant="outline"
        className="w-full border-atlasRed text-atlasRed hover:bg-atlasRed/10"
      >
        <LogOut className="w-4 h-4 mr-2" />
        {t('profile.signOut')}
      </Button>

      <p className="text-center text-atlasTextSecondary text-xs mt-4">
        {t('profile.version')}
      </p>

      {/* Edit Modal */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="glass-card border-atlasBorder sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">{t('profile.editProfile')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-atlasTextSecondary">{t('profile.fullName')}</Label>
              <Input
                data-testid="edit-name-input"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="input-atlas mt-1"
              />
            </div>
            <div>
              <Label className="text-atlasTextSecondary">{t('auth.businessContext')}</Label>
              <Textarea
                data-testid="edit-context-input"
                value={formData.business_context}
                onChange={(e) => setFormData(prev => ({ ...prev, business_context: e.target.value }))}
                className="input-atlas mt-1 min-h-[100px]"
                placeholder={t('auth.businessContextPlaceholder')}
              />
            </div>
            <div>
              <Label className="text-atlasTextSecondary">Secteur d'activité</Label>
              <select
                data-testid="edit-sector-select"
                value={formData.sector || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, sector: e.target.value }))}
                className="input-atlas mt-1 w-full px-3 py-2 rounded-md bg-atlasBg border border-atlasBorder text-white"
              >
                <option value="">— Sélectionne ton secteur —</option>
                {sectors.map(s => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
              <p className="text-xs text-atlasTextSecondary mt-1.5">
                ATLAS active une expertise sectorielle dédiée (KPIs, leviers, vocabulaire métier).
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsEditing(false)} className="text-atlasTextSecondary">
              {t('common.cancel')}
            </Button>
            <Button 
              data-testid="save-profile-btn"
              onClick={handleSave} 
              disabled={saving}
              className="btn-primary"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logout Confirmation */}
      <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <DialogContent className="glass-card border-atlasBorder sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">{t('profile.signOut')}</DialogTitle>
            <DialogDescription className="text-atlasTextSecondary">
              {t('profile.signOutConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowLogoutConfirm(false)} className="text-atlasTextSecondary">
              {t('common.cancel')}
            </Button>
            <Button 
              data-testid="confirm-logout-btn"
              onClick={handleLogout}
              className="bg-atlasRed hover:bg-atlasRed/80 text-white"
            >
              {t('profile.signOut')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------- Phase B1: Engagement push notifications settings ---------- */
const EngagementNotificationsSection = ({ api }) => {
  const push = usePushNotifications(api);
  const [testing, setTesting] = React.useState(false);

  const handleToggle = async () => {
    if (push.isSubscribed) {
      const r = await push.unsubscribe();
      if (r.ok) toast.success('Notifications désactivées sur cet appareil');
    } else {
      const r = await push.subscribe();
      if (r.ok) toast.success('✓ Notifications activées');
      else if (r.reason === 'denied') toast.error('Permission refusée par le navigateur');
      else if (r.reason === 'unsupported') toast.error('Ce navigateur ne supporte pas les notifications push');
      else toast.error('Impossible d\'activer les notifications');
    }
  };

  const handleTest = async () => {
    if (!push.isSubscribed) {
      toast.error('Active d\'abord les notifications');
      return;
    }
    setTesting(true);
    const r = await push.sendTest();
    setTesting(false);
    if (r.ok && r.data?.ok > 0) toast.success('Notification de test envoyée');
    else toast.error('Échec de l\'envoi du test');
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-atlasCyan" />
          <div>
            <p className="text-white font-medium">Notifications d'engagements</p>
            <p className="text-atlasTextSecondary text-sm">
              Rappel 3 min avant chaque engagement
            </p>
          </div>
        </div>
        <button
          data-testid="engagement-push-toggle"
          onClick={handleToggle}
          disabled={push.loading || !push.isSupported}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            push.isSubscribed ? 'bg-atlasCyan' : 'bg-atlasBorder'
          } disabled:opacity-50`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
            push.isSubscribed ? 'translate-x-6' : 'translate-x-0.5'
          }`} />
        </button>
      </div>

      <div className="pl-8 flex flex-col gap-2">
        {!push.isSupported && (
          <p className="text-xs text-amber-400">
            Ton navigateur ne supporte pas les notifications push. Installe ATLAS en PWA sur ton iPhone/Android.
          </p>
        )}
        {push.isSupported && push.isSubscribed && (
          <p className="text-xs text-green-400">✓ Notifications actives sur cet appareil</p>
        )}
        {push.isSupported && !push.isSubscribed && push.permission === 'denied' && (
          <p className="text-xs text-atlasTextSecondary">
            Notifications bloquées par le navigateur — autorise-les dans les réglages du site.
          </p>
        )}
        {push.isSupported && !push.isSubscribed && push.permission !== 'denied' && (
          <p className="text-xs text-atlasTextSecondary">
            Notifications désactivées sur cet appareil
          </p>
        )}
        <button
          data-testid="engagement-push-test-btn"
          onClick={handleTest}
          disabled={!push.isSubscribed || testing}
          className="self-start text-xs text-atlasCyan hover:text-white disabled:opacity-50 disabled:cursor-not-allowed underline-offset-4 hover:underline"
        >
          {testing ? 'Envoi…' : 'Tester une notification'}
        </button>
      </div>
    </div>
  );
};

/* ---------- Phase Pro-actif : Mode Matin / Décision / Soir ---------- */
const ACTIVE_DAYS_OPTIONS = [
  { key: 'sector_default', label: 'Recommandé selon mon secteur' },
  { key: 'all_days', label: '7 jours sur 7' },
  { key: 'weekdays_plus_saturday', label: 'Lundi → Samedi' },
  { key: 'weekdays', label: 'Lundi → Vendredi (week-end off)' },
];

const ModeNotificationsSection = ({ api }) => {
  const [settings, setSettings] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get('/api/mode-notifications/settings')
      .then(r => setSettings(r.data))
      .catch(() => setSettings({
        morning: { enabled: true, hour: 7 },
        evening: { enabled: true, hour: 18 },
        decision: { enabled: true },
        active_days_override: 'sector_default',
        sector: 'default',
      }))
      .finally(() => setLoading(false));
  }, [api]);

  const update = async (patch) => {
    setSaving(true);
    try {
      await api.put('/api/mode-notifications/settings', patch);
      setSettings(prev => ({
        ...prev,
        ...(patch.morning ? { morning: { ...prev.morning, ...patch.morning } } : {}),
        ...(patch.evening ? { evening: { ...prev.evening, ...patch.evening } } : {}),
        ...(patch.decision ? { decision: { ...prev.decision, ...patch.decision } } : {}),
        ...(patch.active_days_override !== undefined ? { active_days_override: patch.active_days_override } : {}),
      }));
    } catch (e) {
      toast.error('Échec de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="text-atlasTextSecondary text-sm">Chargement…</div>;
  }

  const ToggleRow = ({ icon: Icon, title, subtitle, modeKey, hasHour }) => {
    const cfg = settings[modeKey] || {};
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-atlasCyan" />
            <div>
              <p className="text-white font-medium">{title}</p>
              <p className="text-atlasTextSecondary text-sm">{subtitle}</p>
            </div>
          </div>
          <button
            data-testid={`mode-toggle-${modeKey}`}
            onClick={() => update({ [modeKey]: { enabled: !cfg.enabled } })}
            disabled={saving}
            className={`relative w-12 h-6 rounded-full transition-colors ${cfg.enabled ? 'bg-atlasCyan' : 'bg-atlasBorder'} disabled:opacity-50`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${cfg.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
        </div>
        {hasHour && cfg.enabled && (
          <div className="pl-8 flex items-center gap-2">
            <span className="text-xs text-atlasTextSecondary">Heure :</span>
            <select
              data-testid={`mode-hour-${modeKey}`}
              value={cfg.hour ?? (modeKey === 'morning' ? 7 : 18)}
              onChange={(e) => update({ [modeKey]: { hour: parseInt(e.target.value, 10) } })}
              className="bg-atlasBg border border-atlasBorder text-white text-xs rounded px-2 py-1"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}h00</option>
              ))}
            </select>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-white font-medium mb-1">Notifications stratégiques quotidiennes</p>
        <p className="text-atlasTextSecondary text-xs">
          ATLAS te ping aux moments-clés de la journée pour activer le bon mode mental.
        </p>
      </div>

      <ToggleRow
        icon={Sun}
        title="Mode Matin — Activation"
        subtitle="Cadrer LA priorité du jour"
        modeKey="morning"
        hasHour
      />
      <ToggleRow
        icon={Zap}
        title="Mode Décision — Tranchage"
        subtitle="Quand une décision traîne depuis trop longtemps"
        modeKey="decision"
        hasHour={false}
      />
      <ToggleRow
        icon={Moon}
        title="Mode Soir — Décantation"
        subtitle="Débriefer avant de fermer la journée"
        modeKey="evening"
        hasHour
      />

      <div className="pt-3 border-t border-atlasBorder">
        <p className="text-white text-sm font-medium mb-2">Jours actifs</p>
        <select
          data-testid="mode-active-days"
          value={settings.active_days_override || 'sector_default'}
          onChange={(e) => update({ active_days_override: e.target.value })}
          className="w-full bg-atlasBg border border-atlasBorder text-white text-sm rounded px-3 py-2"
        >
          {ACTIVE_DAYS_OPTIONS.map(o => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
        <p className="text-[11px] text-atlasTextSecondary mt-1.5 leading-relaxed">
          Par défaut, ATLAS s'adapte à ton secteur (ex : 7j/7 en restauration, Lundi-Samedi en BTP, Lundi-Vendredi en SaaS).
        </p>
      </div>
    </div>
  );
};

export default Profile;
